import React, { useMemo } from "react";

function hexToHSL(hex) {
    if (!hex || !/^#[0-9a-fA-F]{6}$/.test(hex)) return null;
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h = 0, s = 0;
    const l = (max + min) / 2;
    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        else if (max === g) h = ((b - r) / d + 2) / 6;
        else h = ((r - g) / d + 4) / 6;
    }
    return `hsl(${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%)`;
}

const COLOR_MAP = {
    primary: "--primary", primaryForeground: "--primary-foreground",
    secondary: "--secondary", secondaryForeground: "--secondary-foreground",
    accent: "--accent", accentForeground: "--accent-foreground",
    background: "--background", foreground: "--foreground",
    muted: "--muted", mutedForeground: "--muted-foreground",
    destructive: "--destructive", destructiveForeground: "--destructive-foreground",
    border: "--border", ring: "--ring",
};

export default function DesignSystemPreview({ colors, typography, components }) {
    const cssVars = useMemo(() => {
        const vars = {};
        if (colors) {
            for (const [key, cssVar] of Object.entries(COLOR_MAP)) {
                const hsl = hexToHSL(colors[key]);
                if (hsl) vars[cssVar] = hsl;
            }
        }
        if (components?.borderRadius) vars["--radius"] = components.borderRadius;
        return vars;
    }, [colors, components]);

    const fontStyle = typography?.fontFamily ? { fontFamily: typography.fontFamily } : {};
    const radius = components?.borderRadius || "0.5rem";

    return (
        <div className="border border-input rounded-lg p-6" style={{ ...cssVars, ...fontStyle }}>
            <h3 className="text-sm font-semibold mb-4">Live Preview</h3>
            <div className="space-y-4">
                <div className="p-4 rounded-lg border" style={{
                    backgroundColor: "var(--card, #fff)",
                    color: "var(--card-foreground, #000)",
                    borderColor: "var(--border)",
                    borderRadius: radius,
                }}>
                    <h4 className="font-semibold" style={{ fontWeight: typography?.headingWeight || 700 }}>
                        Sample Card
                    </h4>
                    <p className="text-sm mt-1" style={{
                        color: "var(--muted-foreground)",
                        fontSize: typography?.bodySize || "16px",
                    }}>
                        This preview shows how your design tokens look.
                    </p>
                    <div className="flex gap-2 mt-3">
                        <button className="px-4 py-2 text-sm font-medium" style={{
                            backgroundColor: "var(--primary)",
                            color: "var(--primary-foreground)",
                            borderRadius: radius,
                        }}>
                            Primary
                        </button>
                        <button className="px-4 py-2 text-sm font-medium border" style={{
                            backgroundColor: "var(--secondary)",
                            color: "var(--secondary-foreground)",
                            borderColor: "var(--border)",
                            borderRadius: radius,
                        }}>
                            Secondary
                        </button>
                        <button className="px-4 py-2 text-sm font-medium" style={{
                            backgroundColor: "var(--destructive)",
                            color: "var(--destructive-foreground)",
                            borderRadius: radius,
                        }}>
                            Destructive
                        </button>
                    </div>
                </div>
                <input
                    type="text"
                    placeholder="Sample input field"
                    readOnly
                    className="w-full px-3 py-2 text-sm border"
                    style={{ borderColor: "var(--input)", borderRadius: radius }}
                />
            </div>
        </div>
    );
}
